<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required } from '@vuelidate/validators';
import router from '../../../../index';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { NextButton },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      showModal: true,
      tokenMode: null,
      channelName: '',
      lineChannelId: '',
      lineChannelSecret: '',
      lineChannelToken: '',
      autoRefreshEnabled: false,
    };
  },
  computed: { ...mapGetters({ uiFlags: 'inboxes/getUIFlags' }) },
  validations() {
    const rules = {
      channelName: { required },
      lineChannelId: { required },
      lineChannelSecret: { required },
    };
    if (this.tokenMode === 'long_lived') rules.lineChannelToken = { required };
    return rules;
  },
  methods: {
    selectTokenMode(mode) {
      this.tokenMode = mode;
      this.autoRefreshEnabled = mode === 'short_lived';
      this.showModal = false;
    },
    reopenModal() {
      this.showModal = true;
    },
    goBack() {
      this.showModal = false;
      router.push({ name: 'settings_inbox_new' });
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) return;
      try {
        const channelData = {
          type: 'line',
          line_channel_id: this.lineChannelId,
          line_channel_secret: this.lineChannelSecret,
          token_mode: this.tokenMode,
          auto_refresh_enabled: this.autoRefreshEnabled,
        };
        if (this.tokenMode === 'long_lived') {
          channelData.line_channel_token = this.lineChannelToken;
        }
        const lineChannel = await this.$store.dispatch(
          'inboxes/createChannel',
          {
            name: this.channelName,
            channel: channelData,
          }
        );
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: lineChannel.id },
        });
      } catch (error) {
        useAlert(
          error.response?.data?.message ||
            this.$t('INBOX_MGMT.ADD.LINE_CHANNEL.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="line-page h-full w-full overflow-y-auto">
    <!-- Token-mode modal: rendered with v-if (no external dialog component) to prevent multiple-popup stacking -->
    <div v-if="showModal" class="line-modal-overlay">
      <div class="line-modal">
        <button type="button" class="line-modal__close" @click="goBack">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
        <h2 class="line-modal__title">
          {{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.TITLE') }}
        </h2>
        <p class="line-modal__desc">
          {{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.DESC') }}
        </p>
        <button
          type="button"
          class="line-option"
          :class="{ 'line-option--selected': tokenMode === 'long_lived' }"
          @click="selectTokenMode('long_lived')"
        >
          <div class="line-option__radio">
            <div
              v-if="tokenMode === 'long_lived'"
              class="line-option__radio-dot"
            />
          </div>
          <div class="line-option__body">
            <h3 class="line-option__title">
              {{
                $t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.LONG_LIVED.TITLE')
              }}
            </h3>
            <p class="line-option__desc">
              {{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.LONG_LIVED.DESC') }}
            </p>
            <ul class="line-option__features">
              <li>
                {{
                  $t(
                    'INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.LONG_LIVED.FEATURE_1'
                  )
                }}
              </li>
              <li>
                {{
                  $t(
                    'INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.LONG_LIVED.FEATURE_2'
                  )
                }}
              </li>
              <li>
                {{
                  $t(
                    'INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.LONG_LIVED.FEATURE_3'
                  )
                }}
              </li>
            </ul>
            <span class="line-badge">{{
              $t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.RECOMMENDED')
            }}</span>
          </div>
        </button>
        <button
          type="button"
          class="line-option"
          :class="{ 'line-option--selected': tokenMode === 'short_lived' }"
          @click="selectTokenMode('short_lived')"
        >
          <div class="line-option__radio">
            <div
              v-if="tokenMode === 'short_lived'"
              class="line-option__radio-dot"
            />
          </div>
          <div class="line-option__body">
            <h3 class="line-option__title">
              {{
                $t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.SHORT_LIVED.TITLE')
              }}
            </h3>
            <p class="line-option__desc">
              {{
                $t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.SHORT_LIVED.DESC')
              }}
            </p>
            <ul class="line-option__features">
              <li>
                {{
                  $t(
                    'INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.SHORT_LIVED.FEATURE_1'
                  )
                }}
              </li>
              <li>
                {{
                  $t(
                    'INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.SHORT_LIVED.FEATURE_2'
                  )
                }}
              </li>
              <li>
                {{
                  $t(
                    'INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.SHORT_LIVED.WARNING'
                  )
                }}
              </li>
            </ul>
          </div>
        </button>
      </div>
    </div>

    <!-- Main form -->
    <div v-else class="ch-form-page">
      <div class="ch-form-page__intro">
        <h2 class="ch-form-page__title">
          {{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.TITLE') }}
        </h2>
        <p class="ch-form-page__desc">
          {{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.DESC') }}
        </p>
      </div>
      <form @submit.prevent="createChannel()">
        <div class="ch-form-row">
          <div class="ch-form-row__label-col">
            <label class="ch-form-label">{{
              $t('INBOX_MGMT.ADD.LINE_CHANNEL.CHANNEL_NAME.LABEL')
            }}</label>
          </div>
          <div class="ch-form-row__control-col">
            <input
              v-model="channelName"
              type="text"
              class="ch-input"
              :class="{ 'ch-input--error': v$.channelName.$error }"
              :placeholder="
                $t('INBOX_MGMT.ADD.LINE_CHANNEL.CHANNEL_NAME.PLACEHOLDER')
              "
              @blur="v$.channelName.$touch"
            />
            <span v-if="v$.channelName.$error" class="ch-form-error">{{
              $t('INBOX_MGMT.ADD.LINE_CHANNEL.CHANNEL_NAME.ERROR')
            }}</span>
          </div>
        </div>
        <div class="ch-form-row">
          <div class="ch-form-row__label-col">
            <label class="ch-form-label">{{
              $t('INBOX_MGMT.ADD.LINE_CHANNEL.LINE_CHANNEL_ID.LABEL')
            }}</label>
          </div>
          <div class="ch-form-row__control-col">
            <input
              v-model="lineChannelId"
              type="text"
              class="ch-input"
              :placeholder="
                $t('INBOX_MGMT.ADD.LINE_CHANNEL.LINE_CHANNEL_ID.PLACEHOLDER')
              "
              @blur="v$.lineChannelId.$touch"
            />
          </div>
        </div>
        <div class="ch-form-row">
          <div class="ch-form-row__label-col">
            <label class="ch-form-label">{{
              $t('INBOX_MGMT.ADD.LINE_CHANNEL.LINE_CHANNEL_SECRET.LABEL')
            }}</label>
          </div>
          <div class="ch-form-row__control-col">
            <input
              v-model="lineChannelSecret"
              type="text"
              class="ch-input"
              :placeholder="
                $t(
                  'INBOX_MGMT.ADD.LINE_CHANNEL.LINE_CHANNEL_SECRET.PLACEHOLDER'
                )
              "
              @blur="v$.lineChannelSecret.$touch"
            />
          </div>
        </div>
        <div v-if="tokenMode === 'long_lived'" class="ch-form-row">
          <div class="ch-form-row__label-col">
            <label class="ch-form-label">{{
              $t('INBOX_MGMT.ADD.LINE_CHANNEL.LINE_CHANNEL_TOKEN.LABEL')
            }}</label>
          </div>
          <div class="ch-form-row__control-col">
            <input
              v-model="lineChannelToken"
              type="text"
              class="ch-input"
              :class="{ 'ch-input--error': v$.lineChannelToken?.$error }"
              :placeholder="
                $t('INBOX_MGMT.ADD.LINE_CHANNEL.LINE_CHANNEL_TOKEN.PLACEHOLDER')
              "
              @blur="v$.lineChannelToken?.$touch"
            />
            <span v-if="v$.lineChannelToken?.$error" class="ch-form-error">{{
              $t('INBOX_MGMT.ADD.LINE_CHANNEL.LINE_CHANNEL_TOKEN.ERROR')
            }}</span>
          </div>
        </div>
        <div class="ch-form-row">
          <div class="ch-form-row__label-col" />
          <div class="ch-form-row__control-col">
            <div class="line-mode-bar">
              <span class="line-mode-bar__label">{{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.SELECTED') }}:
                <strong>{{
                  tokenMode === 'long_lived'
                    ? $t(
                        'INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.LONG_LIVED.TITLE'
                      )
                    : $t(
                        'INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.SHORT_LIVED.TITLE'
                      )
                }}</strong></span>
              <button
                type="button"
                class="line-mode-bar__change"
                @click="reopenModal"
              >
                {{ $t('INBOX_MGMT.ADD.LINE_CHANNEL.TOKEN_MODE.CHANGE') }}
              </button>
            </div>
          </div>
        </div>
        <div class="ch-form-footer">
          <NextButton
            :is-loading="uiFlags.isCreating"
            type="submit"
            solid
            teal
            :label="$t('INBOX_MGMT.ADD.LINE_CHANNEL.SUBMIT_BUTTON')"
          />
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.line-page {
  background: #ffffff;
  position: relative;
}
.line-modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.line-modal {
  background: #ffffff;
  border-radius: 16px;
  padding: 28px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
}
.line-modal__close {
  position: absolute;
  top: 14px;
  right: 14px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f3f4f6;
  border: none;
  border-radius: 50%;
  color: #6b7280;
  cursor: pointer;
}
.line-modal__close:hover {
  background: #e5e7eb;
  color: #111827;
}
.line-modal__title {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px;
}
.line-modal__desc {
  font-size: 13px;
  color: #4b5563;
  margin: 0 0 20px;
}
.line-option {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  width: 100%;
  padding: 16px;
  margin-bottom: 12px;
  background: #ffffff;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: all 0.15s ease;
}
.line-option:hover {
  border-color: #0066ff;
  background: #fff7ed;
}
.line-option--selected {
  border-color: #0066ff;
  background: #fff7ed;
}
.line-option__radio {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
}
.line-option--selected .line-option__radio {
  border-color: #0066ff;
}
.line-option__radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #0066ff;
}
.line-option__body {
  flex: 1;
}
.line-option__title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px;
}
.line-option__desc {
  font-size: 12px;
  color: #4b5563;
  margin: 0 0 8px;
}
.line-option__features {
  list-style: none;
  margin: 0 0 8px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.line-option__features li {
  font-size: 12px;
  color: #4b5563;
}
.line-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: #dcfce7;
  color: #16a34a;
}
.ch-form-page {
  padding: 28px 32px 80px;
  max-width: 860px;
  background: #ffffff;
  box-sizing: border-box;
}
.ch-form-page__intro {
  margin-bottom: 24px;
  padding-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
}
.ch-form-page__title {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
}
.ch-form-page__desc {
  font-size: 13px;
  color: #4b5563;
  margin: 0;
}
.ch-form-row {
  display: flex;
  gap: 24px;
  padding: 16px 0;
  border-bottom: 1px solid #f3f4f6;
}
.ch-form-row:last-of-type {
  border-bottom: none;
}
.ch-form-row__label-col {
  width: 240px;
  flex-shrink: 0;
}
.ch-form-row__control-col {
  flex: 1;
  min-width: 0;
}
.ch-form-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px;
}
.ch-input {
  display: block;
  width: 100%;
  max-width: 400px;
  padding: 8px 12px;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #111827;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}
.ch-input:focus {
  border-color: #0066ff;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
}
.ch-input--error {
  border-color: #ef4444;
}
.ch-form-error {
  font-size: 12px;
  color: #ef4444;
  margin: 4px 0 0;
  display: block;
}
.ch-form-footer {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
}
.line-mode-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  max-width: 400px;
}
.line-mode-bar__label {
  font-size: 13px;
  color: #4b5563;
}
.line-mode-bar__label strong {
  color: #111827;
}
.line-mode-bar__change {
  font-size: 13px;
  font-weight: 600;
  color: #0066ff;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}
.line-mode-bar__change:hover {
  color: #0058d8;
  text-decoration: underline;
}
</style>
